import type {
  ContactInfoChannelsValues,
  ContactInfoProfileValues,
  ContactInfoVisitorIntelligenceValues,
  ContactIntelligenceRowData,
} from "@jugl-web/domain-resources/contacts/utils";
import {
  getChannelsSectionRows,
  getProfileSectionRows,
  getVistorIntelligenceSectionRows,
} from "@jugl-web/domain-resources/contacts/utils";
import type { CRMChannel } from "@jugl-web/rest-api";
import { Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import React, { useCallback, useMemo } from "react";
import { ReactComponent as AIIcon } from "./assets/ai.svg";
import { ReactComponent as ChannelsIcon } from "./assets/channels.svg";
import { ReactComponent as ConfidenceIcon } from "./assets/confidence.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ContactIntelligenceFieldRow } from "./components/ContactIntelligenceFieldRow";

type ContactIntelligenceInfoSectionVariant = "default" | "preview";

type ContactIntelligenceInfoSectionProps = {
  variant?: ContactIntelligenceInfoSectionVariant;
  hideHeader?: boolean;
} & (
  | {
      sectionId: "profile";
      values: ContactInfoProfileValues;
    }
  | {
      sectionId: "visitorIntelligence";
      values: ContactInfoVisitorIntelligenceValues;
    }
  | {
      sectionId: "channels";
      values: ContactInfoChannelsValues;
      channelConnectionStatus?: Record<CRMChannel, boolean>;
    }
);

type VisitorIntelligenceSectionProps = Extract<
  ContactIntelligenceInfoSectionProps,
  { sectionId: "visitorIntelligence" }
>;

type ChannelsSectionProps = Extract<
  ContactIntelligenceInfoSectionProps,
  { sectionId: "channels" }
>;

const isVisitorIntelligenceSection = (
  sectionProps: ContactIntelligenceInfoSectionProps
): sectionProps is VisitorIntelligenceSectionProps =>
  sectionProps.sectionId === "visitorIntelligence";

const isChannelsSection = (
  sectionProps: ContactIntelligenceInfoSectionProps
): sectionProps is ChannelsSectionProps =>
  sectionProps.sectionId === "channels";

export const ContactIntelligenceInfoSection: React.FC<
  ContactIntelligenceInfoSectionProps
> = (props) => {
  const { sectionId, values, variant = "default", hideHeader = false } = props;
  const visitorIntelligenceSection = isVisitorIntelligenceSection(props)
    ? props
    : undefined;
  const channelsSection = isChannelsSection(props) ? props : undefined;
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();

  const handleConversationNavigate = useCallback(
    ({ channel, convId }: { channel: CRMChannel; convId: string }) => {
      navigateToPage("clientSupportActiveConversation", {
        channel,
        activeConversation: convId,
      });
    },
    [navigateToPage]
  );

  const rows = useMemo((): ContactIntelligenceRowData[] => {
    if (sectionId === "profile") {
      return getProfileSectionRows(values, t);
    }

    if (sectionId === "visitorIntelligence") {
      return getVistorIntelligenceSectionRows(values, t);
    }

    return getChannelsSectionRows(
      values,
      t,
      channelsSection?.channelConnectionStatus
    );
  }, [channelsSection, sectionId, t, values]);

  const { HeaderIcon, title } = useMemo(() => {
    if (sectionId === "profile") {
      return {
        HeaderIcon: UserIcon,
        title: t({
          id: "contacts-page.customer-info.profile-section",
          defaultMessage: "Profile Info",
        }),
      };
    }

    if (sectionId === "visitorIntelligence") {
      return {
        HeaderIcon: AIIcon,
        title: t({
          id: "contacts-page.customer-info.visitor-intelligence",
          defaultMessage: "Visitor intelligence",
        }),
      };
    }

    return {
      HeaderIcon: ChannelsIcon,
      title: t({
        id: "contacts-page.customer-info.channels-section",
        defaultMessage: "Channels",
      }),
    };
  }, [sectionId, t]);

  const confidence = visitorIntelligenceSection?.values.confidence;

  const content = (
    <div
      className={cx(
        hideHeader
          ? "flex flex-col gap-5"
          : cx(
              "grid gap-[21px] px-[18px] pb-[21px]",
              variant === "preview" ? "grid-cols-2" : "grid-cols-1"
            )
      )}
    >
      {rows.map(({ id, label, value }) => (
        <ContactIntelligenceFieldRow
          key={id}
          label={label}
          value={value}
          onConversationNavigate={handleConversationNavigate}
        />
      ))}
    </div>
  );

  if (hideHeader) {
    return content;
  }

  return (
    <div className="flex w-full flex-col gap-[18px] overflow-hidden rounded-[11px] border border-solid border-grey-300 bg-white">
      <div
        className="flex h-12 shrink-0 items-center justify-between gap-2 px-[14px]"
        style={{
          background:
            "linear-gradient(90deg, #F7F4FA 0%, #F6FDFF 100%), linear-gradient(90deg, #F4F7FA 0%, #FDFDFD 100%), linear-gradient(90deg, #F4FAF4 0%, #FDFDFD 100%)",
        }}
      >
        <div className="flex items-center gap-2">
          <span className="flex h-4 w-4 shrink-0 items-center justify-center text-dark-600 [&_svg]:h-full [&_svg]:w-full">
            <HeaderIcon />
          </span>
          <span className="font-secondary text-xs font-medium text-dark">
            {title}
          </span>
        </div>
        {typeof confidence === "number" && (
          <Tooltip
            isDisabled={variant === "preview"}
            renderTrigger={({ props: tooltipProps, ref }) => (
              <div
                className="flex h-[30px] cursor-default items-center gap-1.5 rounded-[10px] border border-solid border-dark-100 bg-white px-3"
                ref={ref}
                {...tooltipProps}
              >
                <ConfidenceIcon />
                <span className="font-secondary text-xs text-dark-900">
                  {t({
                    id: "contacts-page.confidence",
                    defaultMessage: "Confidence",
                  })}
                  :{" "}
                  <span className="text-[13px] font-medium">
                    {confidence}/10
                  </span>
                </span>
              </div>
            )}
          >
            {t({
              id: "contacts-page.confidence-tooltip",
              defaultMessage:
                "Confidence indicates how reliable the discovered visitor information may be. Lower scores can include incomplete or misleading info ℹ️",
            })}
          </Tooltip>
        )}
      </div>
      {content}
    </div>
  );
};
